import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilters } from '../context/FiltersContext';
// Removed legacy GlobalFilters in favor of Header + FiltersSheet
import KpiSection from './KpiSection';
import OtherIndicatorsSection from './OtherIndicatorsSection';
import SectionAccordion from './Layout/SectionAccordion';
import { SkeletonSection } from './feedback/Skeleton';

const DashboardView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { value: globalFilters } = useFilters();
  const [kpiId, setKpiId] = React.useState<string | null>('N15504');


  // Ensure page is at top on mount or when key filters change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [globalFilters.skolaId]);

  // Handle KPI parameter from URL (filters are handled by FiltersContext)
  useEffect(() => {
    const kpiParam = searchParams.get('kpi');
    if (kpiParam) setKpiId(kpiParam);
  }, [searchParams]);

  const handleKpiChange = (nextKpiId: string) => setKpiId(nextKpiId);

  // Show skeleton while waiting for filters to be loaded from URL
  if (!globalFilters.kommunId || !globalFilters.skolaId) {
    return (
      <div className="space-y-6 pt-20">
        <div className="w-full max-w-[1400px] mx-auto p-4 md:p-6">
          <div className="space-y-4 lg:space-y-6">
            <SkeletonSection />
            <SkeletonSection />
          </div>
        </div>
      </div>
    );
  }

  // Show prompt when "Alla" schools are selected
  if (globalFilters.skolaId === 'null') {
    return (
      <div className="space-y-6 pt-20">
        <div className="w-full max-w-[1400px] mx-auto p-4 md:p-6">
          <div className="rounded-xl bg-card ring-1 ring-border p-8">
            <div className="text-center">
              <h3 className="text-lg font-medium text-foreground mb-2">Välj en skola</h3>
              <p className="text-muted-foreground">
                Välj en specifik skola i filtret för att se detaljerad KPI-data och analyser.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-20">
      {/* Main content */}
      <div className="w-full max-w-[1400px] mx-auto p-4 md:p-6">
        {globalFilters.kommunId && globalFilters.skolaId ? (
          <div className="space-y-4 lg:space-y-6">
            <SectionAccordion
              title="Nyckeltal"
              defaultOpen
              summary={<div className="text-sm text-muted-foreground">Huvudindikatorer för skolan</div>}
            >
              <KpiSection
                kommunId={globalFilters.kommunId}
                skolaId={globalFilters.skolaId}
                year={globalFilters.year ?? 2024}
                kpiId={kpiId || 'N15504'}
                onKpiChange={handleKpiChange}
                compareScope={(globalFilters.compareScope === 'national' ? 'national' : 'municipality')}
              />
            </SectionAccordion>

            <SectionAccordion
              title="Övriga indikatorer"
              summary={<div className="text-sm text-muted-foreground">Kompletterande mätvärden</div>}
              grid={false}
            >
              <OtherIndicatorsSection
                kommunId={globalFilters.kommunId}
                skolaId={globalFilters.skolaId}
                compareScope={(globalFilters.compareScope === 'national' ? 'national' : 'municipality')}
              />
            </SectionAccordion>
          </div>
        ) : (
          <div className="rounded-xl bg-card ring-1 ring-border p-8">
            <div className="text-center">
              <h3 className="text-lg font-medium text-foreground mb-2">
                Välj kommun och skola
              </h3>
              <p className="text-muted-foreground">
                Använd filtren ovan för att välja kommun och skola för att visa data.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Help button */}
      <div className="fixed bottom-4 right-4">
        <HelpButton />
      </div>
    </div>
  );
};

// Help Button Component
const HelpButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title="Om rapporten"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {/* Help Drawer */}
      {isOpen && (
        <div className="fixed inset-0 z-50" onClick={() => setIsOpen(false)}>
          <div className="absolute right-4 bottom-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Om rapporten</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4 text-sm text-gray-600">
                <p>
                  Rapporten visar nyckeltal kring elevresultat, elevenkäter och organisation i kommunala grundskolor.
                  Syftet med rapporten är att främja analys av och dialog kring skolornas resultat och förutsättningar
                  och genom det utgöra ett underlag i det systematiska kvalitetsarbetet.
                </p>
                <div>
                  <p className="font-medium text-gray-900">Skapad av Urban Edlund</p>
                  <p className="text-xs text-slate-500 mb-2">
                    Utvecklad för att ge beslutsfattare och rektorer ett modernt verktyg för skoldataanalys.
                  </p>
                  <a href="https://www.linkedin.com/in/urban-edlund-data-specialist/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">LinkedIn: Urban Edlund</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


export default DashboardView;